import {create} from 'zustand';
import {Agent, AgentType} from '../../../shared/agent';
import {useSaveAgent} from '../../../hooks/api/agents';
import {useMutation} from '@tanstack/react-query';
import {ObjectId} from 'bson';
import _ from 'lodash';
import {ensure} from '../../../shared/util';
import {
  AiEnrichmentAccountFilter,
  AutoEnrichSchedule,
  isAiEnrichmentAccountFilter,
} from '../../../shared/fields/enrichment';
import {AgentLabel} from '../../../shared/validation';

interface AgentBuilderState {
  updateServerState: ({
    agent,
    updateMode,
  }: {
    agent: Agent;
    updateMode: 'always' | 'ifNoChanges';
  }) => void;
  serverState: Agent | undefined;

  hasChanges: () => boolean;

  label: string;
  setLabel: (label: string) => void;

  type: AgentType | undefined;
  setType: (type: AgentType) => void;

  previewAccountIds: ObjectId[];
  addAccount: (accountId: ObjectId) => void;
  removeAccount: (accountId: ObjectId) => void;

  accountFilter: AiEnrichmentAccountFilter | undefined;
  setAccountFilter: (accountFilter: AiEnrichmentAccountFilter) => void;

  autoEnrichNewAccounts: boolean | undefined;
  setAutoEnrichNewAccounts: (autoEnrichNewAccounts: boolean) => void;

  autoEnrichSchedule: AutoEnrichSchedule | undefined;
  setAutoEnrichSchedule: (
    autoEnrichSchedule: AutoEnrichSchedule | undefined
  ) => void;

  isSaveable: () => boolean;
  isValid: () => boolean;
  reset: () => void;
}

const useAgentBuilderState = create<AgentBuilderState>()((set, get) => ({
  updateServerState: ({agent, updateMode}) => {
    if (updateMode === 'ifNoChanges' && get().hasChanges()) {
      // Don't update the server state if there are local changes
      return;
    }

    set({
      serverState: agent,
      label: agent.label,
      type: agent.type,
      previewAccountIds: agent.previewAccountIds,
      accountFilter: agent.refreshSettings?.accountFilter,
      autoEnrichNewAccounts:
        agent.refreshSettings?.autoEnrichNewAccounts ?? true,
      autoEnrichSchedule: agent.refreshSettings?.autoEnrichSchedule,
    });
  },
  serverState: undefined,

  hasChanges: () => {
    const {
      serverState,
      label,
      type,
      previewAccountIds,
      accountFilter,
      autoEnrichNewAccounts,
      autoEnrichSchedule,
    } = get();
    if (!serverState) {
      return !!label || !!type || !!previewAccountIds.length;
    }

    return (
      serverState.label !== label ||
      serverState.type !== type ||
      !_.isEqual(serverState.previewAccountIds, previewAccountIds) ||
      !_.isEqual(serverState.refreshSettings?.accountFilter, accountFilter) ||
      serverState.refreshSettings?.autoEnrichNewAccounts !==
        autoEnrichNewAccounts ||
      !_.isEqual(
        serverState.refreshSettings?.autoEnrichSchedule,
        autoEnrichSchedule
      )
    );
  },

  label: '',
  setLabel: (label) => set({label}),

  type: undefined,
  setType: (type) => set({type}),

  previewAccountIds: [],
  addAccount: (accountId) => {
    const {previewAccountIds} = get();
    if (previewAccountIds.some((id) => id.equals(accountId))) {
      return;
    }

    set({previewAccountIds: [...previewAccountIds, accountId]});
  },
  removeAccount: (accountId) =>
    set({
      previewAccountIds: get().previewAccountIds.filter(
        (id) => !id.equals(accountId)
      ),
    }),

  accountFilter: undefined,
  setAccountFilter: (accountFilter) => set({accountFilter}),

  autoEnrichNewAccounts: undefined,
  setAutoEnrichNewAccounts: (autoEnrichNewAccounts) =>
    set({autoEnrichNewAccounts}),

  autoEnrichSchedule: undefined,
  setAutoEnrichSchedule: (autoEnrichSchedule) => set({autoEnrichSchedule}),

  isSaveable: () => {
    const {accountFilter, label, type} = get();

    const isSaveableAccountFilter =
      // Empty account filter is valid if the agent is not published
      !accountFilter || isAiEnrichmentAccountFilter(accountFilter);

    const trimmedLabel = label.trim();
    return (
      trimmedLabel.length >= AgentLabel.minLength &&
      trimmedLabel.length <= AgentLabel.maxLength &&
      AgentLabel.pattern.test(trimmedLabel) &&
      !!type &&
      isSaveableAccountFilter
    );
  },
  isValid: () => {
    const {accountFilter} = get();
    return get().isSaveable() && isAiEnrichmentAccountFilter(accountFilter);
  },
  reset: () =>
    set({
      accountFilter: undefined,
      autoEnrichNewAccounts: undefined,
      autoEnrichSchedule: undefined,
      serverState: undefined,
      label: '',
      type: undefined,
      previewAccountIds: [],
    }),
}));

export const useAgentBuilderStore = () => {
  const store = useAgentBuilderState();

  const saveAgent = useSaveAgent();
  const saveChanges = useMutation({
    mutationFn: async () => {
      if (!store.hasChanges()) {
        return store.serverState;
      }

      if (!store.isSaveable()) {
        throw new Error('Invalid agent state');
      }

      const {
        accountFilter,
        autoEnrichNewAccounts,
        autoEnrichSchedule,
        label,
        type,
        previewAccountIds,
        serverState,
      } = store;
      return saveAgent.mutateAsync(
        {
          agent: {
            id: serverState?.id,
            label: label.trim(),
            type: type!,
            previewAccountIds,
            status: serverState?.status ?? 'draft',
            ...(accountFilter
              ? {
                  refreshSettings: {
                    accountFilter,
                    ...(autoEnrichNewAccounts !== undefined
                      ? {autoEnrichNewAccounts}
                      : {}),
                    ...(autoEnrichSchedule !== undefined
                      ? {autoEnrichSchedule}
                      : {}),
                  },
                }
              : {}),
          },
        },
        {
          onSuccess: (agent) => {
            store.updateServerState({agent, updateMode: 'always'});
          },
        }
      );
    },
  });

  return {
    ...store,
    saveChanges,
  };
};

export const useAgentBuilderServerState = () => {
  const {serverState} = useAgentBuilderStore();
  return ensure(serverState, 'Agent builder server state is required');
};
